// fetchDomainTrackerResource.js
'use strict';

import fetch from '../resource/customFetch.js';
import Delay from '../resource/Delay.js';
import { domainTracker } from '../resource/debug.js';

import fetchDomainTrackerResourceList from '../action/fetchDomainTrackerResourceList.js';

import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getOperationData from '../selector/getOperationData.js';
import { DOMAIN_TRACKER_LABEL } from '../RemoteConfigKeys.js';

import {
  MERGE_OPERATION_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

const log = domainTracker.extend('log:fetchDomainTrackerResource');
const errorLog = domainTracker.extend('error:fetchDomainTrackerResource');

/**
 * Fetch domain tracker resource
 * @kind action
 * @param {string} {resourceType} - resource type, ex: 'landing', 'cdn_enc'.
 * @return {Promise} Action promise.
 */
const fetchDomainTrackerResource =
  ({ resourceType }) =>
  async (dispatch, getState) => {
    if (!resourceType) {
      errorLog('missing required data', { resourceType });
      return dispatch({ type: '' });
    }

    // fetch resource list
    let state = getState();
    const label = getRemoteConfigData(state, DOMAIN_TRACKER_LABEL);
    const resourceListSelectPath = [
      'domainTracker',
      resourceType,
      label,
      'resourceList',
    ];
    let resourceList = getOperationData(state, resourceListSelectPath);
    if (!resourceList) {
      log('resource list not found, fetching', { resourceType });
      await dispatch(fetchDomainTrackerResourceList({ resourceType }));
      state = getState();
      resourceList = getOperationData(state, resourceListSelectPath);
      log('resource list found', { resourceType, resourceList });
    }

    const raceSelectPath = [
      'domainTracker',
      resourceType,
      label,
      'fastestResource',
    ];

    try {
      dispatch({
        type: SET_NETWORKING_FETCHING,
        payload: { selectPath: raceSelectPath },
      });
      log('fetching fastest resource', { resourceType, resourceList });

      const fastestResource = await Promise.race(
        Object.keys(resourceList).map(async resourceId => {
          const resource = resourceList[resourceId];
          const url = new URL(resource.health_check_path, resource.url);

          const fetchOptions = {
            method: resource.health_check_method,
          };

          const selectPath = [
            'domainTracker',
            resourceType,
            label,
            'resourceList',
            resourceId,
          ];
          dispatch({
            type: SET_NETWORKING_FETCHING,
            payload: { selectPath },
          });

          const startTime = Date.now();
          log('fetching resource', { url: url.href });
          const response = await fetch(url.href, fetchOptions);
          const endTime = Date.now();
          const responseTimeMsec = endTime - startTime;
          log('resource fetched', { url: url.href, responseTimeMsec });

          dispatch({
            type: MERGE_OPERATION_DATA,
            payload: { selectPath, data: { responseTimeMsec } },
          });

          if (response.ok) {
            dispatch({
              type: SET_NETWORKING_SUCCESS,
              payload: { selectPath },
            });
          } else {
            errorLog('fetch error', { url: url.href, status: response.status });
            dispatch({
              type: SET_NETWORKING_ERROR,
              payload: { selectPath, error: new Error('Fetch error') },
            });
            // if all resource failed,
            // wait 5 seconds and choose the fastest failed resource
            await new Delay(5000).promise();
          }
          return resourceId;
        })
      );
      log('fastest resource found', { resourceType, fastestResource });

      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['domainTracker', resourceType, label],
          data: { fastestResource },
        },
      });

      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: raceSelectPath },
      });
    } catch (error) {
      errorLog('fetch fastest resource error', {
        resourceType,
        resourceList,
        error,
      });
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: raceSelectPath, error },
      });
    }
  };

export default fetchDomainTrackerResource;
